import { FormEvent } from "react"
import { Link, useForm, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import type { PageProps } from "@/types/inertia"

export interface LifetimeDealFormValues {
  name: string
  slug: string
  price_cents: number
  max_quantity: number | null
  expires_at: string | null
  active: boolean
}

export function LifetimeDealForm({
  deal,
  action,
  method,
  submitLabel,
}: {
  deal?: LifetimeDealFormValues
  action: string
  method: "post" | "patch"
  submitLabel: string
}) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const form = useForm({
    name: deal?.name ?? "",
    slug: deal?.slug ?? "",
    price: deal ? centsToDollarString(deal.price_cents) : "",
    max_quantity: deal?.max_quantity != null ? String(deal.max_quantity) : "",
    expires_at: deal?.expires_at ? deal.expires_at.slice(0, 10) : "",
    active: deal?.active ?? true,
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({
      name: data.name,
      slug: data.slug,
      price_cents: Math.round(parseFloat(data.price || "0") * 100),
      max_quantity: data.max_quantity,
      expires_at: data.expires_at,
      active: data.active,
    }))
    form.submit(method, action)
  }

  return (
    <form onSubmit={submit} className="mt-8 max-w-lg space-y-6">
      <div className="space-y-2">
        <label htmlFor="deal-name">Name</label>
        <Input
          id="deal-name"
          required
          value={form.data.name}
          aria-invalid={!!errors.name}
          onChange={(e) => form.setData("name", e.target.value)}
        />
        {errors.name && <p className="text-xs text-danger-display">{errors.name}</p>}
      </div>

      <div className="space-y-2">
        <label htmlFor="deal-slug">Slug</label>
        <Input
          id="deal-slug"
          required
          value={form.data.slug}
          aria-invalid={!!errors.slug}
          placeholder="founders"
          onChange={(e) => form.setData("slug", e.target.value)}
        />
        <p className="text-xs text-ink-muted">
          Lowercase letters, numbers, and dashes. The checkout URL becomes /buy?ldt=slug.
        </p>
        {errors.slug && <p className="text-xs text-danger-display">{errors.slug}</p>}
      </div>

      <div className="space-y-2">
        <label htmlFor="deal-price">Price (USD)</label>
        <Input
          id="deal-price"
          type="number"
          min="0"
          step="0.01"
          required
          value={form.data.price}
          aria-invalid={!!errors.price_cents}
          placeholder="49.00"
          onChange={(e) => form.setData("price", e.target.value)}
        />
        <p className="text-xs text-ink-muted">Set to 0 for a free deal.</p>
        {errors.price_cents && <p className="text-xs text-danger-display">{errors.price_cents}</p>}
      </div>

      <div className="space-y-2">
        <label htmlFor="deal-max-quantity">Max redemptions (optional)</label>
        <Input
          id="deal-max-quantity"
          type="number"
          min="1"
          step="1"
          value={form.data.max_quantity}
          aria-invalid={!!errors.max_quantity}
          placeholder="Unlimited"
          onChange={(e) => form.setData("max_quantity", e.target.value)}
        />
        {errors.max_quantity && <p className="text-xs text-danger-display">{errors.max_quantity}</p>}
      </div>

      <div className="space-y-2">
        <label htmlFor="deal-expires-at">Expires on (optional)</label>
        <Input
          id="deal-expires-at"
          type="date"
          value={form.data.expires_at}
          aria-invalid={!!errors.expires_at}
          onChange={(e) => form.setData("expires_at", e.target.value)}
        />
        {errors.expires_at && <p className="text-xs text-danger-display">{errors.expires_at}</p>}
      </div>

      <label htmlFor="deal-active" className="flex items-center gap-2 font-normal text-ink-body">
        <Checkbox
          id="deal-active"
          checked={form.data.active}
          onChange={(e) => form.setData("active", e.target.checked)}
        />
        Active (redeemable at checkout)
      </label>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={form.processing}>
          {submitLabel}
        </Button>
        <Link href="/admin/lifetime_deals" className="text-sm text-ink-muted no-underline hover:text-ink-display">
          Cancel
        </Link>
      </div>
    </form>
  )
}

function centsToDollarString(cents: number) {
  const dollars = cents / 100
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2)
}
